import React, { useState } from 'react';
import { useRegion } from '../contexts/RegionContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useTheme } from '../contexts/ThemeContext';
import { RegionCode } from '../types/region';
import { LanguageCode } from '../types/language';
import { REGIONS } from '../utils/currencyData';
import { LANGUAGES, REGION_DEFAULT_LANGUAGES } from '../utils/languageData';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Globe, Check } from 'lucide-react';
import { tr } from '../i18n/text';

interface LocaleSelectorProps {
  variant?: 'default' | 'compact';
}

interface LocalePreset {
  region: RegionCode;
  language: LanguageCode;
  label: string;
}

const LOCALE_PRESETS: LocalePreset[] = [
  { region: 'IN', language: 'en', label: 'India - English' },
  { region: 'IN', language: 'hi', label: 'India - हिन्दी' },
  { region: 'US', language: 'en', label: 'USA - English' },
  { region: 'US', language: 'es', label: 'USA - Español' },
  { region: 'GB', language: 'en', label: 'UK - English' },
  { region: 'EU', language: 'en', label: 'Europe - English' },
  { region: 'EU', language: 'es', label: 'España - Español' },
  { region: 'AE', language: 'ar', label: 'UAE - العربية' },
  { region: 'AE', language: 'en', label: 'UAE - English' },
];

export function LocaleSelector({ variant = 'default' }: LocaleSelectorProps) {
  const { region, setRegion, flag: regionFlag, currencySymbol } = useRegion();
  const { language, setLanguage, flag: languageFlag, nativeName } = useLanguage();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';
  const [open, setOpen] = useState(false);

  const handlePresetChange = (preset: LocalePreset) => {
    setRegion(preset.region);
    setLanguage(preset.language);
    setOpen(false);
  };

  const handleRegionChange = (newRegion: RegionCode) => {
    // Region only sets prices/currency; the language stays what the visitor chose (it's in the URL).
    setRegion(newRegion);
  };

  const handleLanguageChange = (newLanguage: LanguageCode) => {
    setLanguage(newLanguage);
  };

  const popularRegions: RegionCode[] = ['IN', 'US', 'GB', 'EU', 'AU'];
  const otherRegions: RegionCode[] = Object.keys(REGIONS).filter(
    (r) => !popularRegions.includes(r as RegionCode) && r !== 'GLOBAL' && r in REGIONS
  ) as RegionCode[];

  // Note: fixed typings by using actual keys
  const popularLanguages: LanguageCode[] = ['en', 'hi', 'es', 'ar'];
  const otherLanguages: LanguageCode[] = Object.keys(LANGUAGES).filter(
    (lang) => ! popularLanguages.includes(lang as LanguageCode)
  ) as LanguageCode[];

  const menuStyle = {
    backgroundColor: isDark ? '#1e293b' : '#ffffff', // Slate-800 or white
    borderColor: isDark ? 'rgba(255,255,255,0.1)' : '#e2e8f0',
    color: isDark ? '#f8fafc' : '#0f172a',
  };

  const hoverStyle = (isSelected: boolean) => ({
    backgroundColor: isSelected 
      ? (isDark ? 'rgba(99,102,241,0.2)' : '#eef2ff') 
      : 'transparent',
  });

  if (variant === 'compact') {
    return (
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="sm" className="gap-2 h-9 px-3 hover:bg-black/5 dark:hover:bg-white/10 rounded-xl" aria-label={tr("Select Region and Language")}>
            <span className="text-lg leading-none">{regionFlag}</span>
            <span className="text-sm font-medium" style={{ color: 'var(--text-muted)' }}>|</span>
            <span className="text-xs font-bold uppercase tracking-wider" style={{ color: 'var(--text-secondary)' }}>{language}</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent 
          align="end" 
          className="w-80 max-h-[80vh] overflow-y-auto shadow-2xl rounded-2xl border"
          style={menuStyle}
        >
          {/* Quick Presets */}
          <DropdownMenuLabel className="text-xs uppercase tracking-wide opacity-50 px-3 py-2">
            {tr("Quick Select")}
          </DropdownMenuLabel>
          <DropdownMenuSeparator style={{ backgroundColor: menuStyle.borderColor }} />
          
          <div className="grid grid-cols-2 gap-1 p-2">
            {LOCALE_PRESETS.map((preset) => {
              const regionData = REGIONS[preset.region];
              const langData = LANGUAGES[preset.language];
              const isSelected = region === preset.region && language === preset.language;
              
              return (
                <DropdownMenuItem
                  key={`${preset.region}-${preset.language}`}
                  onClick={() => handlePresetChange(preset)}
                  className={`flex items-center gap-2 cursor-pointer py-2 px-2 rounded-lg transition-colors`}
                  style={hoverStyle(isSelected)}
                >
                  <span className="text-base leading-none">{regionData.flag}</span>
                  <span className="text-base leading-none">{langData.flag}</span>
                  <span className="text-xs flex-1 truncate font-medium">
                    {preset.label.split(' - ')[1]}
                  </span>
                  {isSelected && <Check className="w-3 h-3 text-indigo-500 flex-shrink-0" />}
                </DropdownMenuItem>
              );
            })}
          </div>

          <DropdownMenuSeparator style={{ backgroundColor: menuStyle.borderColor }} />
          
          {/* Current Selection */}
          <div className="p-3 my-1" style={{ backgroundColor: isDark ? 'rgba(0,0,0,0.2)' : '#f8fafc', borderTop: `1px solid ${menuStyle.borderColor}`, borderBottom: `1px solid ${menuStyle.borderColor}` }}>
            <div className="text-xs uppercase tracking-wide opacity-50 mb-2">{tr("Current")}</div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-lg leading-none">{regionFlag}</span>
                <span className="text-sm font-semibold">
                  {REGIONS[region].name}
                </span>
                <span className="text-xs opacity-60">({currencySymbol})</span>
              </div>
            </div>
            <div className="flex items-center gap-2 mt-2">
              <span className="text-lg leading-none">{languageFlag}</span>
              <span className="text-sm font-semibold">{nativeName}</span>
            </div>
          </div>

          <DropdownMenuSeparator style={{ backgroundColor: menuStyle.borderColor }} />

          {/* Region Selection */}
          <DropdownMenuLabel className="text-xs uppercase tracking-wide opacity-50 px-3 py-2">
            {tr("Change Region")}
          </DropdownMenuLabel>
          
          {popularRegions.map((regionCode) => {
            const regionData = REGIONS[regionCode];
            const isSelected = region === regionCode;
            
            return (
              <DropdownMenuItem
                key={regionCode}
                onClick={() => handleRegionChange(regionCode)}
                className="flex items-center justify-between cursor-pointer py-2.5 px-3 rounded-md mx-1"
                style={hoverStyle(isSelected)}
              >
                <div className="flex items-center gap-3">
                  <span className="text-base leading-none">{regionData.flag}</span>
                  <span className="text-sm font-medium">{regionData.name}</span>
                  <span className="text-xs opacity-50">{regionData.currency}</span>
                </div>
                {isSelected && <Check className="w-4 h-4 text-indigo-500" />}
              </DropdownMenuItem>
            );
          })}

          <DropdownMenuSeparator style={{ backgroundColor: menuStyle.borderColor }} />

          {/* Language Selection */}
          <DropdownMenuLabel className="text-xs uppercase tracking-wide opacity-50 px-3 py-2">
            {tr("Change Language")}
          </DropdownMenuLabel>
          
          {popularLanguages.map((langCode) => {
            const langData = LANGUAGES[langCode];
            const isSelected = language === langCode;
            
            return (
              <DropdownMenuItem
                key={langCode}
                onClick={() => handleLanguageChange(langCode)}
                className="flex items-center justify-between cursor-pointer py-2.5 px-3 rounded-md mx-1 my-0.5"
                style={hoverStyle(isSelected)}
              >
                <div className="flex items-center gap-3">
                  <span className="text-base leading-none">{langData.flag}</span>
                  <span className="text-sm font-medium">{langData.nativeName}</span>
                  <span className="text-xs opacity-50">{langData.name}</span>
                </div>
                {isSelected && <Check className="w-4 h-4 text-indigo-500" />}
              </DropdownMenuItem>
            );
          })}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  // Default variant
  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className="gap-3 transition-colors rounded-xl" style={{ backgroundColor: 'var(--bg-card)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} aria-label={tr("Select Region and Language")}>
          <div className="flex items-center gap-2">
            <span className="text-xl leading-none">{regionFlag}</span>
            <span className="font-semibold">{currencySymbol}</span>
          </div>
          <span className="opacity-30">|</span>
          <div className="flex items-center gap-2">
            <span className="text-xl leading-none">{languageFlag}</span>
            <span className="font-semibold">{nativeName}</span>
          </div>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-96 max-h-[80vh] overflow-y-auto shadow-2xl rounded-2xl border" style={menuStyle}>
        {/* Quick Presets */}
        <DropdownMenuLabel className="text-xs uppercase tracking-wide opacity-50 px-4 py-2">
          {tr("Quick Select Region & Language")}
        </DropdownMenuLabel>
        <DropdownMenuSeparator style={{ backgroundColor: menuStyle.borderColor }} />
        
        <div className="p-2 space-y-1">
          {LOCALE_PRESETS.map((preset) => {
            const regionData = REGIONS[preset.region];
            const langData = LANGUAGES[preset.language];
            const isSelected = region === preset.region && language === preset.language;
            
            return (
              <DropdownMenuItem
                key={`${preset.region}-${preset.language}`}
                onClick={() => handlePresetChange(preset)}
                className="flex items-center justify-between cursor-pointer py-3 px-3 rounded-xl transition-colors"
                style={hoverStyle(isSelected)}
              >
                <div className="flex items-center gap-4">
                  <div className="flex flex-col gap-1 items-center">
                    <span className="text-xl leading-none">{regionData.flag}</span>
                    <span className="text-xl leading-none">{langData.flag}</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="font-semibold">{preset.label}</span>
                    <span className="text-xs opacity-60">
                      {regionData.currency} • {langData.name}
                    </span>
                  </div>
                </div>
                {isSelected && <Check className="w-5 h-5 text-indigo-500" />}
              </DropdownMenuItem>
            );
          })}
        </div>

        <DropdownMenuSeparator style={{ backgroundColor: menuStyle.borderColor }} />
        
        {/* Current Selection */}
        <div className="p-5 my-2" style={{ backgroundColor: isDark ? 'rgba(0,0,0,0.2)' : '#f8fafc', borderTop: `1px solid ${menuStyle.borderColor}`, borderBottom: `1px solid ${menuStyle.borderColor}` }}>
          <div className="text-xs uppercase tracking-wide opacity-50 mb-4">{tr("Current Selection")}</div>
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <span className="text-2xl leading-none">{regionFlag}</span>
              <div>
                <div className="font-semibold">{REGIONS[region].name}</div>
                <div className="text-xs opacity-60">{REGIONS[region].currency} ({currencySymbol})</div>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-2xl leading-none">{languageFlag}</span>
              <div>
                <div className="font-semibold">{nativeName}</div>
                <div className="text-xs opacity-60">{LANGUAGES[language].name}</div>
              </div>
            </div>
          </div>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default LocaleSelector;
